import { useState, useMemo, useId } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { Search, ChevronRight, BookOpen, CreditCard, FileSignature, ShieldCheck, Monitor, MessagesSquare } from 'lucide-react';

// Maps the 19 FAQ entries from marketing.help.q1..q19 into categories.
// The category headings come from marketing.help.cat_* — i18n owns the
// display strings.
const CATEGORIES = [
  { id: 'getting_started', i18nKey: 'cat_getting_started', icon: BookOpen, qs: [1, 2, 3, 4] },
  { id: 'plans',           i18nKey: 'cat_plans',           icon: CreditCard, qs: [5, 6, 7, 8, 9] },
  { id: 'esign',           i18nKey: 'cat_esign',           icon: FileSignature, qs: [10, 11, 12] },
  { id: 'privacy',         i18nKey: 'cat_privacy',         icon: ShieldCheck, qs: [13, 14, 15] },
  { id: 'desktop',         i18nKey: 'cat_desktop',         icon: Monitor, qs: [16, 17, 18, 19] },
];

export default function Help() {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const searchId = useId();

  const normalizedQuery = query.trim().toLowerCase();

  const filtered = useMemo(() => {
    if (!normalizedQuery) return CATEGORIES;
    return CATEGORIES
      .map((cat) => ({
        ...cat,
        qs: cat.qs.filter((n) => {
          const q = (t(`marketing:help.q${n}`) || '').toLowerCase();
          const a = (t(`marketing:help.a${n}`) || '').toLowerCase();
          return q.includes(normalizedQuery) || a.includes(normalizedQuery);
        }),
      }))
      .filter((cat) => cat.qs.length > 0);
  }, [normalizedQuery, t]);

  const totalMatches = filtered.reduce((sum, c) => sum + c.qs.length, 0);

  return (
    <>
      <Helmet>
        <title>{t('marketing:help.meta_title')}</title>
        <meta
          name="description"
          content={t('marketing:help.meta_desc', 'Help center for Scrivar PDF Suite. Browse FAQs about plans, billing, eSign, privacy, and the desktop app.')}
        />
        <link rel="canonical" href="https://scrivar.com/help" />
      </Helmet>

      <div className="bg-gray-50">
        <div>
          {/* Hero with search */}
          <section className="bg-white border-b border-gray-100">
            <div className="max-w-3xl mx-auto px-6 py-14 sm:py-20 text-center">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight">
                {t('marketing:help.hero_heading')}
              </h1>

              <div className="mt-8 relative">
                <label htmlFor={searchId} className="sr-only">
                  {t('marketing:help.search_placeholder')}
                </label>
                <Search
                  size={20}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                  aria-hidden="true"
                />
                <input
                  id={searchId}
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={t('marketing:help.search_placeholder')}
                  className="w-full rounded-xl border border-gray-200 bg-white pl-12 pr-4 py-3.5 text-base text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500"
                  autoComplete="off"
                />
              </div>

              {normalizedQuery && (
                <p className="mt-3 text-sm text-gray-500" role="status" aria-live="polite">
                  {totalMatches === 0
                    ? null
                    : t('marketing:help.matches_count', { count: totalMatches, defaultValue_one: '{{count}} answer', defaultValue_other: '{{count}} answers' })}
                </p>
              )}
            </div>
          </section>

          {/* Categories + FAQs */}
          <section className="max-w-3xl mx-auto px-6 py-12 space-y-10">
            {filtered.length === 0 ? (
              <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-8 text-center">
                <p className="text-gray-700">
                  <Trans
                    i18nKey="marketing:help.no_results"
                    components={{
                      a: <a href="/contact" className="text-indigo-600 font-semibold hover:underline">contact us</a>,
                    }}
                  />
                </p>
              </div>
            ) : (
              filtered.map((cat) => {
                const Icon = cat.icon;
                return (
                  <div key={cat.id}>
                    <div className="flex items-center gap-2 mb-4">
                      <span className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center">
                        <Icon size={16} className="text-indigo-600" />
                      </span>
                      <h2 className="text-lg font-semibold text-gray-900">
                        {t(`marketing:help.${cat.i18nKey}`)}
                      </h2>
                    </div>
                    <div className="bg-white rounded-xl border border-gray-100 shadow-sm divide-y divide-gray-100 overflow-hidden">
                      {cat.qs.map((n) => (
                        <FaqItem
                          key={n}
                          question={t(`marketing:help.q${n}`)}
                          answer={t(`marketing:help.a${n}`)}
                          highlight={normalizedQuery}
                        />
                      ))}
                    </div>
                  </div>
                );
              })
            )}

            {/* Still need help? */}
            <div className="bg-gradient-to-br from-indigo-50 to-white rounded-2xl border border-indigo-100 p-8 text-center">
              <div className="w-12 h-12 mx-auto rounded-xl bg-white border border-indigo-100 flex items-center justify-center mb-4">
                <MessagesSquare size={22} className="text-indigo-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900">
                {t('marketing:help.still_heading')}
              </h3>
              <p className="mt-2 text-gray-600 max-w-md mx-auto">
                {t('marketing:help.still_body')}
              </p>
              <a
                href="/contact"
                className="mt-5 inline-flex items-center gap-2 rounded-lg px-5 py-2.5 text-white font-semibold shadow-sm transition-all hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-indigo-500"
                style={{ background: 'linear-gradient(135deg, #4F46E5, #6366F1)' }}
              >
                {t('marketing:help.still_cta')}
              </a>
            </div>
          </section>
        </div>
      </div>
    </>
  );
}

function FaqItem({ question, answer, highlight }) {
  return (
    <details className="group">
      <summary className="flex items-start justify-between gap-3 px-5 py-4 cursor-pointer list-none hover:bg-gray-50 focus-visible:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-indigo-300">
        <span className="text-gray-900 font-medium leading-snug">
          {highlight ? <HighlightedText text={question} highlight={highlight} /> : question}
        </span>
        <ChevronRight
          size={18}
          className="flex-shrink-0 mt-0.5 text-gray-400 transition-transform group-open:rotate-90"
          aria-hidden="true"
        />
      </summary>
      <div className="px-5 pb-5 -mt-1 text-gray-600 leading-relaxed text-[15px]">
        {highlight ? <HighlightedText text={answer} highlight={highlight} /> : answer}
      </div>
    </details>
  );
}

function HighlightedText({ text, highlight }) {
  if (!highlight) return text;
  const escaped = highlight.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const parts = text.split(new RegExp(`(${escaped})`, 'gi'));
  return parts.map((part, i) =>
    part.toLowerCase() === highlight.toLowerCase() ? (
      <mark key={i} className="bg-yellow-100 text-gray-900 rounded px-0.5">
        {part}
      </mark>
    ) : (
      <span key={i}>{part}</span>
    ),
  );
}
